// ════════════════════════════════════════════════════════════════════
// AUDIT PANEL — the operator's compliance-readiness dashboard.
//
// Three jobs:
//   1. SURFACE GAPS — every client across the book that's missing one
//      or more compliance items (driven by canSubmitEnrollment).
//      Each row shows what's missing + a one-click "fix" pathway.
//   2. ASSEMBLE PACKETS — pick a client → fetch the full audit packet
//      JSON (forms + signatures + checklist + chain verification).
//   3. AUDIT-NOTICE MODE — the operator flips a toggle when they've
//      received an audit notice; the panel emphasizes deadlines, full
//      packets-for-download per client in scope, and "consult legal
//      counsel" guidance per the AUDIT system prompt.
//
// All numbers come from the AUDIT-instrumented endpoints; no number
// is fabricated in the UI.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { ShieldCheck, ShieldAlert, FileSearch, Download, AlertTriangle, RefreshCw, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

export default function AuditPanel({ currentUser, showToast = () => {} }) {
  const [vertical, setVertical] = React.useState('medicare');
  const [data, setData]         = React.useState(null);
  const [loading, setLoading]   = React.useState(true);
  const [briefing, setBriefing] = React.useState(null);
  const [briefingBusy, setBriefingBusy] = React.useState(false);
  const [auditMode, setAuditMode] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/audit/gaps?vertical=${vertical}`);
      setData(r);
    } catch (err) {
      showToast(`Audit panel load failed: ${err.message}`, 'rose');
    } finally { setLoading(false); }
  }, [currentUser?.id, vertical, showToast]);

  React.useEffect(() => { load(); }, [load]);

  async function getBriefing() {
    setBriefingBusy(true);
    try {
      const r = await apiFetch(`/api/audit/gaps?vertical=${vertical}&briefing=true`);
      setBriefing(r.briefing || 'No briefing returned.');
    } catch (e) { showToast(`Briefing failed: ${e.message}`, 'rose'); }
    finally { setBriefingBusy(false); }
  }

  async function downloadPacket(clientId, clientName) {
    try {
      const out = await apiFetch(`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`);
      const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-packet-${(clientName || 'client').replace(/\s+/g, '_')}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      showToast(`Packet download failed: ${e.message}`, 'rose');
    }
  }

  const gaps = data?.gaps || [];
  const scanned = data?.clients_scanned || 0;
  const totalGaps = data?.gaps_total || 0;

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Hero */}
      <div className={`rounded-xl p-5 border ${
        auditMode ? 'bg-rose-50 border-rose-300' : 'bg-white border-slate-200'
      }`}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-slate-900 inline-flex items-center gap-2">
              {auditMode ? <ShieldAlert size={16} className="text-rose-700"/> : <ShieldCheck size={16} className="text-emerald-700"/>}
              AUDIT — Compliance-Readiness
            </h2>
            <div className="text-xs text-slate-600 mt-0.5">
              Continuous gap watch · one-click audit packets · immutable hash-chained trail
            </div>
            <div className="text-xs text-slate-700 mt-2 tabular-nums">
              {scanned} clients scanned · <strong>{totalGaps}</strong> with open compliance gaps · vertical:&nbsp;
              <select value={vertical} onChange={e => setVertical(e.target.value)} className="text-xs border border-slate-300 rounded px-1.5 py-0.5">
                <option value="medicare">Medicare</option>
                <option value="aca">ACA</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={getBriefing}
              disabled={briefingBusy || gaps.length === 0}
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-black disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <FileSearch size={12}/> Tell me what I'm missing
            </button>
            <button
              onClick={load}
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw size={12}/> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* AUDIT-NOTICE MODE TOGGLE */}
      <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
        auditMode ? 'bg-rose-50 border-rose-300' : 'bg-amber-50 border-amber-200'
      }`}>
        <AlertTriangle size={16} className={auditMode ? 'text-rose-700 mt-0.5' : 'text-amber-700 mt-0.5'}/>
        <div className="text-xs leading-relaxed flex-1">
          {auditMode ? (
            <>
              <strong className="text-rose-900">AUDIT-NOTICE MODE.</strong>{' '}
              Identify which clients/records are in scope. Download the packet for each. Compare what was requested vs. what's on file. Flag gaps honestly — do not paper over them. If allegations are significant, <strong>consult a regulatory attorney</strong>.
            </>
          ) : (
            <>
              <strong className="text-amber-900">Standard mode.</strong> Use the gap list below to close items before they become liabilities. Flip to <em>Audit-Notice Mode</em> if you've received an audit notice — that puts the dashboard into response-support mode.
            </>
          )}
        </div>
        <button
          onClick={() => setAuditMode(v => !v)}
          className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${
            auditMode ? 'bg-rose-900 text-white border-rose-900' : 'bg-white text-amber-900 border-amber-300'
          }`}
        >
          {auditMode ? 'Exit' : 'I have an audit notice'}
        </button>
      </div>

      {/* AI BRIEFING */}
      {briefing && (
        <div className="bg-slate-900 text-slate-100 rounded-xl p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 mb-2">AUDIT briefing</div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{briefing}</div>
        </div>
      )}

      {/* GAPS TABLE */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Scanning…</div>
        ) : gaps.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-emerald-700 font-semibold">
            <ShieldCheck size={28} className="mx-auto mb-2 text-emerald-600"/>
            Book is audit-clean for {vertical === 'medicare' ? 'Medicare' : 'ACA'}. No open compliance gaps.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Missing items</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {gaps.map(g => (
                <tr key={g.client_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-900">{g.client_name || g.client_id?.slice(0, 8)}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{g.client_id?.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <ul className="space-y-1">
                      {g.missing.map(m => (
                        <li key={m.key} className="text-[11px] flex items-start gap-1.5">
                          <ChevronRight size={11} className="text-rose-600 mt-0.5 shrink-0"/>
                          <div>
                            <span className="font-bold">{m.label}</span>
                            {m.why && <div className="text-[10px] text-slate-500 italic">{m.why}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => downloadPacket(g.client_id, g.client_name)}
                      className="text-xs font-bold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                      title="Download the full audit packet for this client (JSON)"
                    >
                      <Download size={11}/> Packet
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] text-slate-500 text-center">
        Audit data is server-captured · append-only hash chain · NEVER alters records · escalate to regulatory counsel for serious allegations
      </div>
    </div>
  );
}
